/**
 * OverlayManager (singleton)
 *
 * Purpose
 * -------
 * Centralized manager for a single shared overlay DOM node used across the
 * theme to block background interactions and receive outside-click events.
 * It prevents multiple components from creating competing overlays and
 * provides ownership semantics so multiple components can safely request the
 * overlay without stomping on each other.
 *
 * Key concepts / API
 * ------------------
 * - show(options) -> ownerToken (string)
 *     Request the overlay to be visible. Returns a token representing the
 *     ownership of that visibility request. Callers should keep the token
 *     and pass it to hide(token) when they no longer need the overlay.
 *
 *     options: {
 *       topOffset: number (px)     // shifts the overlay top (useful when
 *                                 // overlay should start below header)
 *       zIndex: number|string      // inline z-index for overlay while owned
 *       before: Element            // insert overlay into DOM before this node
 *     }
 *
 * - hide(token?)
 *     Release a previously-acquired owner token. If a token is provided the
 *     specific owner is removed; if no token is provided the method falls
 *     back to removing a single owner (legacy-compatible behavior).
 *
 * - add(handler, options?) / remove(handler)
 *     Register a click handler that runs when the overlay is clicked. Handlers
 *     are separate from ownership: calling add() makes the overlay visible so
 *     the handler can be clicked, but it does not create an ownership token.
 *     remove() removes a handler. If there are no owners and no handlers the
 *     overlay is hidden.
 *
 * - getState()
 *     Dev helper that returns internal counts (owners, handlers, attached).
 *
 * Semantics & design notes
 * ------------------------
 * - Ownership vs handlers: ownership (show/hide) controls visual state via a
 *   Map of owner tokens. Handlers are callbacks registered separately so a
 *   component can opt-in to outside-click behavior without being responsible
 *   for overlay ownership.
 * - Defensive click behavior: when the overlay is clicked, all registered
 *   handlers will be invoked. The overlay also clears handlers and owner
 *   state defensively to avoid becoming stuck if some caller fails to clean
 *   up. This makes the overlay resilient in mixed/legacy code paths.
 * - z-index restoration: the manager captures the overlay's default
 *   z-index from computed style and restores it on full hide so consumer
 *   modifications don't persist unexpectedly.
 *
 * Accessibility
 * -------------
 * - The overlay sets `aria-hidden` when visible/hidden. For full a11y
 *   compatibility consider making background content inert (or toggling
 *   `aria-hidden` on main content) when a drawer is open. This manager does
 *   not implement `inert` automatically (polyfill required in some browsers).
 *
 * Migration notes
 * ---------------
 * - Old code that called show()/hide() without tokens will continue to work.
 *   New callers should capture the token returned by show() and provide it
 *   to hide(token) for deterministic cleanup. Example:
 *
 *     const owner = OverlayManager.show({ zIndex: 9999, before: drawerEl });
 *     OverlayManager.add(() => closeDrawer());
 *     // later...
 *     OverlayManager.remove(handler);
 *     OverlayManager.hide(owner);
 *
 * - FocusTrap integration: FocusTrap now captures the token returned from
 *   OverlayManager.show() and uses it to hide the overlay on deactivate.
 */

const OverlayManager = (() => {
  // Use a global singleton to ensure all bundles share the same state
  if (window.__OverlayManager) {
    return window.__OverlayManager;
  }

  let overlay = null;
  // ownerId -> options
  const owners = new Map();
  let _nextId = 1;
  const handlers = new Set();

  function place(options = {}) {
    if (options.before?.isConnected && options.before.parentNode) {
      const parent = options.before.parentNode;
      if (overlay.parentNode !== parent || overlay.nextSibling !== options.before) {
        parent.insertBefore(overlay, options.before);
      }
    }
  }

  function ensure() {
    // Legacy callers may place the overlay inside a subtree that is later morphed.
    // Reattach that same node so its theme classes and click listener survive.
    if (overlay && !overlay.isConnected) {
      document.body.appendChild(overlay);
    }
    if (overlay) return overlay;
    overlay = document.querySelector('[data-focus-overlay]');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.setAttribute('data-focus-overlay', '');
      overlay.className =
        'fixed z-9998 top-0 left-0 h-screen min-h-[-webkit-fill-available] w-full bg-black/30 transition-opacity duration-300 pointer-events-none aria-hidden:opacity-0 aria-hidden:duration-0 aria-[hidden=false]:pointer-events-auto aria-[hidden=false]:cursor-pointer';
      overlay.style.position = 'fixed';
      overlay.style.inset = '0';
      overlay.style.zIndex = '9990';
      overlay.style.background = 'rgba(0, 0, 0, 0.3)';
      overlay.style.pointerEvents = 'none';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.setAttribute('aria-label', 'Close');
      overlay.setAttribute('role', 'button');
      document.body.appendChild(overlay);
    }

    // Remember the default z-index so we can restore it when the overlay is hidden.
    // Read from the inline style set at creation time — avoids a forced style recalc.
    if (typeof overlay._defaultZIndex === 'undefined') {
      overlay._defaultZIndex = overlay.style.zIndex || '';
    }

    if (!overlay._attached) {
      // Use passive listener for better scroll performance, capture for faster response
      overlay.addEventListener('click', (e) => {
        // Call all registered handlers (copy to avoid mutation issues)
        const copy = Array.from(handlers);
        for (const fn of copy) {
          try { fn(e); } catch (err) { console.error('[OverlayManager] handler error', err); }
        }
      }, { passive: true, capture: false });
      overlay._attached = true;
    }

    return overlay;
  }

  function restoreAfterMorph() {
    if (!overlay || overlay.isConnected) return;

    const shouldRestore = owners.size > 0 || handlers.size > 0;
    if (!shouldRestore) return;

    ensure();

    const options = Array.from(owners.values()).pop() || {};
    if (options.topOffset) {
      overlay.style.top = `${options.topOffset}px`;
      overlay.style.height = `calc(100% - ${options.topOffset}px)`;
    } else {
      overlay.style.top = '';
      overlay.style.height = '';
    }
    if (options.zIndex) {
      overlay.style.zIndex = String(options.zIndex);
    }
    try { place(options); } catch (err) { /* ignore placement errors */ }

    overlay.style.pointerEvents = 'auto';
    overlay.setAttribute('aria-hidden', 'false');
  }

  document.addEventListener('theme:section:morph', restoreAfterMorph);
  document.addEventListener('shopify:section:load', restoreAfterMorph);

  return {
    add(handler, options = {}) {
      if (typeof handler !== 'function') return;
      ensure();
      // Apply visual options if provided when registering
      if (options.topOffset) {
        overlay.style.top = `${options.topOffset}px`;
        overlay.style.height = `calc(100% - ${options.topOffset}px)`;
      }
      if (options.zIndex) {
        overlay.style.zIndex = String(options.zIndex);
      }
      try { place(options); } catch (err) { /* ignore placement errors */ }
      handlers.add(handler);
      // Do not modify the refcount when registering a handler. show()/hide()
      // manages the visual refcount. Ensure overlay is visible so handler can be clicked.
      overlay.style.pointerEvents = 'auto';
      overlay.setAttribute('aria-hidden', 'false');
    },
    remove(handler) {
      if (!overlay) return;
      if (typeof handler === 'function') handlers.delete(handler);
      // Only hide if there are no show() owners and no handlers left
      if (owners.size === 0 && handlers.size === 0) {
        overlay.setAttribute('aria-hidden', 'true');
        overlay.style.top = '';
        overlay.style.height = '';
        // restore default z-index on full hide
        overlay.style.zIndex = overlay._defaultZIndex || '';
        overlay.style.pointerEvents = 'none';
        // Return to a stable body-level position now that nothing owns the overlay —
        // `place()` may have parked it inside a section (e.g. a drawer rendered inline
        // in blocks/pdp-options.liquid) for DOM-order stacking. Left there while idle,
        // a later section re-render (variant switch) would diff it as an ordinary
        // child and corrupt it. See show()/place().
        if (overlay.parentNode !== document.body) document.body.appendChild(overlay);
      }
    },
    show(options = {}) {
      // options: { topOffset: number (px), zIndex: string/number, before: Element }
      ensure();
      const id = String(_nextId++);
      owners.set(id, options || {});

      // apply visual options for the most recently added owner
      if (options.topOffset) {
        overlay.style.top = `${options.topOffset}px`;
        overlay.style.height = `calc(100% - ${options.topOffset}px)`;
      } else {
        overlay.style.top = '';
        overlay.style.height = '';
      }
      if (options.zIndex) {
        overlay.style.zIndex = String(options.zIndex);
      }
      try { place(options); } catch (err) { /* ignore placement errors */ }

      overlay.style.pointerEvents = 'auto';
      overlay.setAttribute('aria-hidden', 'false');
      return id;
    },
    hide(token) {
      if (!overlay) return;

      // Backwards compatible: if no token provided, behave like previous
      // hide() by removing one arbitrary owner (the most recent), otherwise
      // remove the given token.
      if (typeof token === 'undefined') {
        // remove a single owner if present
        const iter = owners.keys();
        const first = iter.next();
        if (!first.done) owners.delete(first.value);
      } else {
        owners.delete(String(token));
      }

      // Only hide overlay if no show() owners and no registered handlers
      if (owners.size === 0 && handlers.size === 0) {
        overlay.setAttribute('aria-hidden', 'true');
        overlay.style.top = '';
        overlay.style.height = '';
        overlay.style.zIndex = overlay._defaultZIndex || '';
        overlay.style.pointerEvents = 'none';
        // See remove() — park back under <body> so an idle overlay can't be caught
        // inside a section that re-renders later (e.g. a variant-switch morph).
        if (overlay.parentNode !== document.body) document.body.appendChild(overlay);
      } else {
        // If there are still owners, apply the last owner's visual options
        const last = Array.from(owners.values()).pop();
        if (last) {
          if (last.topOffset) {
            overlay.style.top = `${last.topOffset}px`;
            overlay.style.height = `calc(100% - ${last.topOffset}px)`;
          } else {
            overlay.style.top = '';
            overlay.style.height = '';
          }
          if (last.zIndex) overlay.style.zIndex = String(last.zIndex);
        }
      }
    },
    // Debug helper - return internal state useful for tests or debugging
    getState() {
      return {
        owners: owners.size,
        handlers: handlers.size,
        attached: !!(overlay && overlay._attached),
        ariaHidden: overlay ? overlay.getAttribute('aria-hidden') : null
      };
    },
    getOverlay() {
      if (overlay) return overlay;
      const found = document.querySelector('[data-focus-overlay]');
      if (found) overlay = found;
      return overlay;
    }
  };
})();

// Store globally so all bundles share the same instance
if (!window.__OverlayManager) {
  window.__OverlayManager = OverlayManager;
}

export default OverlayManager;
